import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Input,
  TabPanel,
  TabPanels,
  VStack,
} from '@chakra-ui/react';
import { disabledInputStyles, Text } from '@diamond/shared/ui';
import { PillTabs } from '@diamond/sol-admin/components';
import {
  useCustomerTypes,
  useDistributionChannel,
} from '@diamond/sol-admin/features/shipto';
import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { TARGET_VOUCHER_OPTIONS } from '../../constants';
import { useBusinessCategories, useVoucherTargetForm } from '../../hooks';
import type { CreateVoucherRequest } from '../../types';
import { GeneralTargetSection } from './voucher-target/general-target-section';
import { ShipToTargetSection } from './voucher-target/ship-to-target-section';

interface VoucherTargetFormProps {
  form: UseFormReturn<CreateVoucherRequest>;
  disabled?: boolean;
}

export function VoucherTargetForm({
  form,
  disabled = false,
}: VoucherTargetFormProps) {
  const {
    register,
    formState: { errors },
  } = form;

  // Fetch options from API
  const { options: customerTypeOptions, isLoading: isLoadingCustomerTypes } =
    useCustomerTypes();
  const { options: branchOptions, isLoading: isLoadingBranches } =
    useDistributionChannel();
  const {
    options: restaurantCategoryOptions,
    isLoading: isLoadingRestaurantCategories,
  } = useBusinessCategories();

  const isLoadingOptions =
    isLoadingCustomerTypes || isLoadingBranches || isLoadingRestaurantCategories;

  const {
    targetType,
    targetTabIndex,
    selectedCustomerTypes,
    selectedBranches,
    selectedRestaurantCategories,
    isCsvUploading,
    csvUploadUrl,
    csvUploadStats,
    handleTargetTabChange,
    handleCustomerTypesChange,
    handleBranchesChange,
    handleRestaurantCategoriesChange,
    handleCsvUpload,
    handleShipToRemove,
    handleDownloadShipToTemplate,
  } = useVoucherTargetForm({ form });

  const customerTypeValue = useMemo(
    () =>
      customerTypeOptions.filter((option) =>
        selectedCustomerTypes.includes(option.value)
      ),
    [customerTypeOptions, selectedCustomerTypes]
  );

  const branchValue = useMemo(
    () =>
      branchOptions.filter((option) =>
        selectedBranches.includes(option.value)
      ),
    [branchOptions, selectedBranches]
  );

  const restaurantCategoryValue = useMemo(
    () =>
      restaurantCategoryOptions.filter((option) =>
        selectedRestaurantCategories.includes(option.value)
      ),
    [restaurantCategoryOptions, selectedRestaurantCategories]
  );

  const isShipToTarget = targetType === 'SHIP_TO';

  return (
    <VStack spacing={6} align="stretch">
      <Text fontSize="lg" fontWeight="semibold" color="gray.800">
        Target Pengguna
      </Text>

      <Grid
        templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
        gap={4}
      >
        <GridItem>
          <FormControl isInvalid={!!errors.target_configuration?.quota}>
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
              Qty. Voucher Beredar
            </FormLabel>
            <Input
              type="number"
              min={1}
              placeholder="Masukkan kuantitas voucher yang beredar"
              fontSize="sm"
              {...register('target_configuration.quota', {
                valueAsNumber: true,
              })}
              isDisabled={disabled}
              sx={disabledInputStyles}
            />
            <FormErrorMessage>
              {errors.target_configuration?.quota?.message as string}
            </FormErrorMessage>
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl
            isInvalid={!!errors.target_configuration?.max_use_per_user}
          >
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
              Maks. pakai per shipto.
            </FormLabel>
            <Input
              type="number"
              min={1}
              placeholder="Masukkan batas pakai per shipto"
              fontSize="sm"
              {...register('target_configuration.max_use_per_user', {
                valueAsNumber: true,
              })}
              isDisabled={disabled}
              sx={disabledInputStyles}
            />
            <FormErrorMessage>
              {errors.target_configuration?.max_use_per_user?.message as string}
            </FormErrorMessage>
          </FormControl>
        </GridItem>
      </Grid>

      <FormControl isInvalid={!!errors.target_configuration?.target_type}>
        <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
          Target Voucher
        </FormLabel>
        <PillTabs
          tablist={TARGET_VOUCHER_OPTIONS.map((option) => ({
            name: option.label,
          }))}
          index={targetTabIndex}
          onChange={disabled ? () => undefined : handleTargetTabChange}
          isDisabled={disabled}
        >
          <TabPanels display="none">
            {TARGET_VOUCHER_OPTIONS.map((option) => (
              <TabPanel key={option.value} p={0} />
            ))}
          </TabPanels>
        </PillTabs>
        <FormErrorMessage>
          {errors.target_configuration?.target_type?.message as string}
        </FormErrorMessage>
      </FormControl>

      {isShipToTarget ? (
        <ShipToTargetSection
          errors={errors}
          isUploading={isCsvUploading}
          csvUploadUrl={csvUploadUrl}
          csvUploadStats={csvUploadStats}
          onDownloadTemplate={handleDownloadShipToTemplate}
          onUpload={handleCsvUpload}
          onRemove={handleShipToRemove}
          disabled={disabled}
        />
      ) : (
        <GeneralTargetSection
          register={register}
          errors={errors}
          customerTypeOptions={customerTypeOptions}
          customerTypeValue={customerTypeValue}
          branchOptions={branchOptions}
          branchValue={branchValue}
          restaurantCategoryOptions={restaurantCategoryOptions}
          restaurantCategoryValue={restaurantCategoryValue}
          onCustomerTypesChange={handleCustomerTypesChange}
          onBranchesChange={handleBranchesChange}
          onRestaurantCategoriesChange={handleRestaurantCategoriesChange}
          isLoadingOptions={isLoadingOptions}
          disabled={disabled}
          form={form}
        />
      )}
    </VStack>
  );
}
